// src/Dashboard/pages/teacher/PrepaPhysique/components/ExerciseDataTable.jsx

import React from "react";
import {
  FaDumbbell,
  FaExternalLinkAlt,
  FaInfoCircle,
  FaPen,
  FaTrashAlt,
  FaUserFriends,
} from "react-icons/fa";

import { htmlToText, truncateText } from "../utils/prepaPhysiqueUtils";

export default function ExerciseDataTable({
  exercises = [],
  loading,
  deletingId,
  onView,
  onEdit,
  onDelete,
  onAssign,
}) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
          />
        ))}
      </div>
    );
  }

  if (!exercises.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-800/40">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-white text-slate-400 shadow-sm dark:bg-slate-900">
          <FaDumbbell />
        </div>

        <h3 className="mt-4 text-base font-bold text-slate-900 dark:text-white">
          Aucun exercice trouvé
        </h3>

        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Modifiez vos filtres ou ajoutez un nouvel exercice.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950 md:block">
        <table className="min-w-[1100px] w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <th className="px-4 py-3">Exercice</th>
              <th className="px-4 py-3">Programme</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3">Image</th>
              <th className="px-4 py-3">Vidéo</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {exercises.map((exercise) => {
              const deleting = deletingId === `exercise-${exercise.id}`;
              const preview = truncateText(htmlToText(exercise.description), 150);

              return (
                <tr
                  key={exercise.id}
                  className="bg-white transition hover:bg-slate-50 dark:bg-slate-950 dark:hover:bg-slate-900"
                >
                  <td className="px-4 py-3">
                    <div className="font-bold text-slate-900 dark:text-white">
                      {exercise.nom || "—"}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/60">
                      {exercise.programme || "Sans programme"}
                    </span>
                  </td>

                  <td className="max-w-[380px] px-4 py-3 text-slate-600 dark:text-slate-300">
                    {preview || "—"}
                  </td>

                  <td className="px-4 py-3">
                    {exercise.lien_photo ? (
                      <img
                        src={exercise.lien_photo}
                        alt={exercise.nom || "Illustration"}
                        className="h-16 w-16 rounded-xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                      />
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {exercise.lien_video ? (
                      <a
                        href={exercise.lien_video}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-3 py-2 text-xs font-bold text-blue-700 ring-1 ring-blue-200 transition hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-200 dark:ring-blue-800/60"
                      >
                        Vidéo
                        <FaExternalLinkAlt />
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <IconButton
                        label="Détails"
                        icon={<FaInfoCircle />}
                        onClick={() => onView?.(exercise)}
                        className="bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                      />

                      <IconButton
                        label="Affecter"
                        icon={<FaUserFriends />}
                        onClick={() => onAssign?.(exercise)}
                        className="bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
                      />

                      <IconButton
                        label="Modifier"
                        icon={<FaPen />}
                        onClick={() => onEdit?.(exercise)}
                        className="bg-indigo-600 text-white hover:bg-indigo-700"
                      />

                      <IconButton
                        label={deleting ? "Suppression..." : "Supprimer"}
                        icon={<FaTrashAlt />}
                        onClick={() => onDelete?.(exercise)}
                        disabled={deleting}
                        className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 md:hidden">
        {exercises.map((exercise) => {
          const preview = truncateText(htmlToText(exercise.description), 120);

          return (
            <article
              key={exercise.id}
              className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950"
            >
              <div className="flex gap-3">
                <img
                  src={exercise.lien_photo || "/img/no-image.jpg"}
                  alt={exercise.nom || "Illustration"}
                  className="h-16 w-16 rounded-xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                />

                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-bold text-slate-900 dark:text-white">
                    {exercise.nom || "—"}
                  </h3>

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {exercise.programme || "Sans programme"}
                  </p>

                  {preview && (
                    <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-600 dark:text-slate-300">
                      {preview}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-3 grid grid-cols-4 gap-2">
                <MobileButton icon={<FaInfoCircle />} onClick={() => onView?.(exercise)} />
                <MobileButton icon={<FaUserFriends />} onClick={() => onAssign?.(exercise)} />
                <MobileButton icon={<FaPen />} onClick={() => onEdit?.(exercise)} />
                <MobileButton
                  icon={<FaTrashAlt />}
                  onClick={() => onDelete?.(exercise)}
                  danger
                />
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
}

function IconButton({ icon, label, onClick, className = "", disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`inline-flex h-10 w-10 items-center justify-center rounded-xl text-sm font-bold transition ${className}`}
    >
      {icon}
    </button>
  );
}

function MobileButton({ icon, onClick, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-11 items-center justify-center rounded-xl text-sm font-bold transition ${
        danger
          ? "bg-red-600 text-white hover:bg-red-700"
          : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
      }`}
    >
      {icon}
    </button>
  );
}